import { useAuthStore } from '@/store/auth';
import { useAgentStatusStore } from '@/store/agentStatus';
import { useVoipSocketStore } from '@/store/voipSocket';
import { getToken } from '@/utils/cookies';
import { SUPER_ADMIN } from '@/constants/userGroups';

/**
 * Authentication middleware that checks if user is authenticated
 * before allowing access to protected routes.
 *
 * Token strategy:
 * - Full session token is stored in main cookie (voip-token).
 * - Temporary 2FA token is stored only in temp cookie (voip-token-temp).
 * This ensures direct URL / new-tab access correctly redirects to VerifyOtp
 * (sessionStorage would be empty in a new tab and cannot be relied on).
 */
const checkAuth = async ({ to, next, router }) => {
  const authStore = useAuthStore();
  const token = getToken();
  const tempToken = getToken(true);
  const isAuthenticated = !!token;
  const hasTempTokenOnly = !token && !!tempToken;

  // Check if URL has auth_key parameter
  const authKey = to.query.auth_key;
  if (authKey) {
    try {
      await authStore.loginWithAuthKey(authKey);

      // Redirect based on user group
      const isSuperAdmin = authStore.user?.user_group?.id === SUPER_ADMIN;
      const dashboardRoute = isSuperAdmin
        ? 'AdminDashboard'
        : 'AutoCallDashboard';
      return next({ name: dashboardRoute });
    } catch (error) {
      console.error('Failed to login with auth_key:', error);
    }
  }

  // Define public routes that don't require authentication
  const publicRoutes = ['Login', 'ForgotPassword', 'ResetPassword'];
  const isPublicRoute = publicRoutes.includes(to.name);

  // No token at all → login (unless public route)
  if (!isPublicRoute && !isAuthenticated && !hasTempTokenOnly) {
    return next({ name: 'Login' });
  }

  // 2FA pending (temp token only, e.g. new tab / direct URL): only VerifyOtp is allowed
  if (hasTempTokenOnly && to.name !== 'VerifyOtp') {
    return next({ name: 'VerifyOtp' });
  }

  // VerifyOtp page: allow if temp token only; if full token, redirect to dashboard
  if (to.name === 'VerifyOtp') {
    if (isAuthenticated) {
      const isSuperAdmin = authStore.user?.user_group?.id === SUPER_ADMIN;
      const dashboardRoute = isSuperAdmin
        ? 'AdminDashboard'
        : 'AutoCallDashboard';
      return next({ name: dashboardRoute });
    }
    if (hasTempTokenOnly) {
      return next();
    }
    return next({ name: 'Login' });
  }

  // Fetch user data if authenticated but user not loaded yet
  if (isAuthenticated) {
    if (!authStore.user || !authStore.user.user_group) {
      await authStore.getUser(true);
    }
    // Single WebSocket connect: register agent-status handlers then connect
    useAgentStatusStore().registerWithVoipSocket();
    useVoipSocketStore().connect();
  }

  // Fully authenticated user on login page → redirect to dashboard
  if (isAuthenticated && to.name === 'Login') {
    const isSuperAdmin = authStore.user?.user_group?.id === SUPER_ADMIN;
    const dashboardRoute = isSuperAdmin
      ? 'AdminDashboard'
      : 'AutoCallDashboard';
    return next({ name: dashboardRoute });
  }

  return next();
};

export default checkAuth;
